import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { api } from '../../lib/api';
import { isPipState, formatPricePerLocation, PROVIDER_TYPES } from '../../lib/pipStates';
import { getReferral } from '../../lib/referral';
import { isFirmLike } from '../../lib/orgTypes';
import { TRIAL_DAYS } from '../../lib/trial';
import TrialTimeline from '../billing/TrialTimeline';
import MarketerOnboarding from './MarketerOnboarding';
import AddressAutocomplete from '../shared/AddressAutocomplete';
import { loadStripe } from '@stripe/stripe-js';
import { Scale, Stethoscope, Check, X, ArrowRight, ArrowLeft, Eye, EyeOff, ShieldCheck, Loader2, IdCard, Play } from 'lucide-react';
import './OrgSignupPage.css';

// Version of the BAA presented at signup. Must match the file served by
// LegalDocPage (public/legal/baa-<version>.md) — an acceptance record is only
// meaningful if the exact text it refers to can still be produced.
export const BAA_VERSION = '2026-10-23';

// Single stripe-js promise so we don't re-init on every render.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Invite-only: a platform admin's invite lands here with ?type=marketer (and
// pending_org_type on the auth user). Same flow as a law firm, free; never
// shown in the public picker below.
const MARKETER_TYPE = {
  value: 'marketer', label: 'Marketer', Icon: Scale, iconClass: 'law',
  description: 'Works with several law firms and always has patients to refer',
  features: ['Case management', 'Client intake', 'Referral concierge', 'Referral tracking'],
  price: 'Free', priceNote: 'By invitation', cta: 'Get started',
};

const ORG_TYPES = [
  {
    value: 'law_firm',
    label: 'Law Firm',
    Icon: Scale,
    iconClass: 'law',
    description: 'Personal injury attorneys, paralegals, and legal staff',
    features: ['Case management', 'Client intake', 'Document generation', 'Settlement tracking'],
    price: 'Free',
    priceNote: 'Always free for referring attorneys',
    cta: 'Get started',
  },
  {
    value: 'medical_practice',
    label: 'Medical Practice',
    Icon: Stethoscope,
    iconClass: 'medical',
    description: 'Physicians, specialists, therapists, and clinical staff',
    features: [
      'Patient scheduling & treatment tracking',
      'Cross-org collaboration with law firms',
      'Forensic accounting & anti-fraud module',
      'Unlimited staff users',
    ],
    // Pricing now opens during the demo conversation rather than being
    // pinned to the picker card. The Stripe checkout further on still
    // uses the canonical $1,000 / $2,500 per-location price; we just don't
    // anchor a number on the marketing surface anymore.
    price: `${TRIAL_DAYS} days`,
    priceNote: 'free to start, no card · pricing tailored to your practice',
    cta: 'Get started',
  },
];

const STEP_LABELS = ['Type', 'Account', 'Practice', 'Review'];

// 40-second Vimeo walkthroughs, one per org type — surfaced on the type
// picker so people can see the whole signup before committing to it.
const ONBOARDING_VIDEOS = {
  law_firm:         { id: '1206853322', title: 'Law firm onboarding — 40-second tour' },
  medical_practice: { id: '1206840423', title: 'Medical provider onboarding — 40-second tour' },
};

export default function OrgSignupPage() {
  const { session, signIn } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [orgType, setOrgType] = useState(null);
  const [authMode, setAuthMode] = useState(session ? null : 'signup');
  const [userForm, setUserForm] = useState({ firstName: '', lastName: '', email: '', password: '', phone: '' });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [orgForm, setOrgForm] = useState({ name: '', address_line1: '', city: '', state: '', zip: '', phone: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Which org type's walkthrough video is open in the lightbox (null = closed).
  const [videoFor, setVideoFor] = useState(null);
  const [baaAccepted, setBaaAccepted] = useState(false);
  useEffect(() => {
    if (!videoFor) return;
    const onKey = (e) => { if (e.key === 'Escape') setVideoFor(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [videoFor]);

  // Resume: a signed-in user's saved draft (any device) plus their identity
  // status. Restores the form, the NPI check, the BAA tick and jumps to the
  // furthest step reached — a returning prospect never starts over.
  const [resumed, setResumed] = useState(null); // { at, rebuilt } once a draft was restored
  const [resumeChecked, setResumeChecked] = useState(!session);
  useEffect(() => {
    if (!session || resumeChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getSignupResume();
        if (cancelled || r.has_membership) return;
        if (r.user) setUserForm((prev) => ({ ...prev, firstName: r.user.first_name || prev.firstName, lastName: r.user.last_name || prev.lastName, email: r.user.email || prev.email, phone: r.user.phone || prev.phone }));
        if (r.identity?.verified) {
          setIdStatus(r.identity);
          if (r.identity.verified_name) {
            const parts = r.identity.verified_name.trim().split(/\s+/);
            setIdEditFirstName(parts[0] || ''); setIdEditLastName(parts.slice(1).join(' ') || '');
          }
        }
        const d = r.draft;
        if (d?.org_type) {
          setOrgType(d.org_type);
          if (d.org_form) setOrgForm((prev) => ({ ...prev, ...d.org_form }));
          if (d.provider_type) setProviderType(d.provider_type);
          if (d.npi) setNpi(d.npi);
          setBaaAccepted(!!d.baa_accepted);
          setResumed({ at: r.draft_at, rebuilt: d.rebuilt_from === 'stripe_checkout' });
          // The NPI gate is server-checked at checkout anyway; re-run it
          // quietly so the step-3 gate reflects the registry, not a flag.
          if (d.npi && d.npi_verified) {
            api.verifyNpi(d.npi).then(({ record }) => { if (!cancelled) { setNpiRecord(record); setNpiVerified(true); } }).catch(() => {});
          }
          const identityOkNow = process.env.REACT_APP_DEMO === 'true' || (r.identity?.verified && r.identity?.name_match !== false);
          setStep(d.step >= 4 && d.npi_verified && identityOkNow ? 4 : 3);
        }
      } catch { /* no draft, or endpoint unavailable — start normally */ }
      finally { if (!cancelled) setResumeChecked(true); }
    })();
    return () => { cancelled = true; };
  }, [session, resumeChecked]); // eslint-disable-line

  // NPI verification — medical_practice only. We hold a verified
  // CMS-NPPES record so step 3 can prefill the practice details from
  // the official registry and the step 3 → 4 button can hard-block
  // until the practice is confirmed real.
  // Provider type — medical_practice only. Recorded on the org; it no longer
  // changes the price (the state does: PIP states $2,500, others $1,000).
  const [providerType, setProviderType] = useState('');

  const [npi, setNpi] = useState('');
  const [npiVerified, setNpiVerified] = useState(false);
  const [npiRecord, setNpiRecord] = useState(null);
  const [npiVerifying, setNpiVerifying] = useState(false);
  const [npiError, setNpiError] = useState('');

  // Reset NPI state if the user changes back to law_firm (no NPI required
  // there) or edits the NPI after a successful verify — they need to
  // re-verify before continuing.
  const resetNpiVerification = () => {
    setNpiVerified(false);
    setNpiRecord(null);
    setNpiError('');
  };

  // Stripe Identity (medical_practice only) — proves the signing user is
  // a real human with a real government-issued ID. The actual modal is
  // Stripe's hosted UI; after it closes we poll our backend (which the
  // webhook has updated) until verified or until we give up retrying.
  const [idVerifying, setIdVerifying] = useState(false);
  const [idStatus, setIdStatus] = useState(null);   // { verified, name_match, account_name, verified_name }
  const [idError, setIdError] = useState('');
  // Inline form fields for the "update your account name to match your
  // ID" fix-up path — defaulted to the verified name on first render.
  const [idEditFirstName, setIdEditFirstName] = useState('');
  const [idEditLastName, setIdEditLastName] = useState('');
  const [idUpdating, setIdUpdating] = useState(false);

  useEffect(() => {
    if (!session || !resumeChecked || step < 3 || !orgType) return;
    const t = setTimeout(() => {
      api.saveSignupDraft({ org_type: orgType, org_form: orgForm, provider_type: providerType, npi, npi_verified: npiVerified, baa_accepted: baaAccepted, step }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [session, resumeChecked, step, orgType, orgForm, providerType, npi, npiVerified, baaAccepted]);

  const updateUser = (f, v) => setUserForm(p => ({ ...p, [f]: v }));
  const updateOrg  = (f, v) => setOrgForm(p => ({ ...p, [f]: v }));

  // Password rules — live-checked, shown as a requirements list under the
  // password input on signup. All four must pass for the form to submit.
  const pwChecks = useMemo(() => {
    const p = userForm.password || '';
    return {
      length:    p.length >= 8,
      uppercase: /[A-Z]/.test(p),
      number:    /\d/.test(p),
      match:     p.length > 0 && p === confirmPassword,
    };
  }, [userForm.password, confirmPassword]);
  const pwAllValid = pwChecks.length && pwChecks.uppercase && pwChecks.number && pwChecks.match;

  const selectedType = ORG_TYPES.find(t => t.value === orgType) || (orgType === 'marketer' ? MARKETER_TYPE : undefined);
  const firmNoun = orgType === 'marketer' ? 'business' : 'firm';

  // DEMO environment: skip NPI + Stripe Identity so trainers/prospects can
  // create orgs instantly. The server bypasses the matching gates when
  // DEMO_MODE is on (see server/config/demoMode.js). No-op in prod — this
  // flag is only set on the demo build (REACT_APP_DEMO=true).
  const DEMO = process.env.REACT_APP_DEMO === 'true';
  const npiOk = DEMO || npiVerified;
  const identityOk = DEMO || (!!idStatus?.verified && idStatus?.name_match !== false);

  // Per-location price depends on the state: PIP / no-fault states bill
  // premium, every other state standard. Shows $1,000 until a state is typed.
  const perLocationPrice = formatPricePerLocation(orgForm.state, providerType || undefined);
  // The PIP/no-fault call-out explains why this state is the higher rate.
  // Suppressed in demo, where every location bills the flat premium rate.
  const isPipPriced = !DEMO && isPipState(orgForm.state);

  // Invited marketer: skip the type picker. The link carries ?type=marketer
  // and the invite stamped pending_org_type on the auth user; the server
  // refuses a marketer org without that stamp, so this is convenience only.
  useEffect(() => {
    if (orgType) return;
    const fromUrl = new URLSearchParams(window.location.search).get('type');
    const fromAuth = session?.user?.user_metadata?.pending_org_type;
    if (fromUrl === 'marketer' || fromAuth === 'marketer') { setOrgType('marketer'); setStep(session ? 3 : 2); }
  }, [orgType, session]); // eslint-disable-line

  const handleTypeSelect = (type) => {
    setOrgType(type);
    setError('');
    setStep(session ? 3 : 2);
  };

  // A 401 from any signup-flow endpoint means the browser's session went
  // stale (expired refresh token, signed out elsewhere) even though
  // AuthContext still holds one — the exact state that used to dead-end
  // people at step 3. Send them back to the account step to sign in again.
  const recoverFromExpiredSession = (err) => {
    if (err?.status !== 401) return false;
    setAuthMode('signin');
    setStep(2);
    setError('Your session expired — please sign in again to continue.');
    return true;
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);

    if (authMode === 'signup') {
      if (!pwAllValid) {
        setError('Please satisfy all password requirements.');
        setLoading(false); return;
      }
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      try {
        const res = await fetch(`${API_URL}/auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: userForm.email,
            password: userForm.password,
            first_name: userForm.firstName,
            last_name: userForm.lastName,
            phone: userForm.phone || undefined,
            // Referral attribution (first-touch, from localStorage) — stamped
            // on the user so pre-org abandoners are still attributable.
            referral_source: getReferral()?.code || null,
          }),
        });
        const result = await res.json();
        if (!res.ok) { setError(result.error); setLoading(false); return; }
        if (result.session) {
          await supabase.auth.setSession({
            access_token: result.session.access_token,
            refresh_token: result.session.refresh_token,
          });
        }
      } catch (err) {
        setError(err.message); setLoading(false); return;
      }
    } else {
      const { error: err } = await signIn(userForm.email, userForm.password);
      if (err) { setError(err.message); setLoading(false); return; }
    }

    setLoading(false);
    setStep(3);
  };

  // Hit /api/signup/verify-npi → Luhn check + live CMS NPPES lookup.
  // On success: prefill the practice's legal name + address from the
  // official record (user can still edit), and mark the form gate-open.
  // On failure: surface the precise reason and keep them gated.
  const handleVerifyNpi = async () => {
    const cleaned = (npi || '').replace(/[^\d]/g, '');
    if (cleaned.length !== 10) {
      setNpiError('NPI must be exactly 10 digits.');
      return;
    }
    setNpiVerifying(true); setNpiError('');
    try {
      const { record } = await api.verifyNpi(cleaned);
      setNpiRecord(record);
      setNpiVerified(true);
      setNpi(record.npi);
      // Prefill from the CMS record. Don't clobber values the user has
      // already typed — let them keep a DBA name or a different mailing
      // address if they prefer.
      setOrgForm(prev => ({
        name: prev.name || record.legal_name || '',
        address_line1: prev.address_line1 || record.address?.line1 || '',
        city:          prev.city          || record.address?.city  || '',
        state:         prev.state         || record.address?.state || '',
        zip:           prev.zip           || record.address?.zip   || '',
        phone:         prev.phone,
      }));
    } catch (err) {
      setNpiVerified(false);
      setNpiRecord(null);
      setNpiError(err.message || 'Verification failed.');
    } finally {
      setNpiVerifying(false);
    }
  };

  // Kick off Stripe Identity. We create a verification session on the
  // backend, hand the client_secret to Stripe's hosted modal, then poll
  // our status endpoint until the webhook has flipped users.
  // identity_verified_at (or until we give up — the webhook is the
  // source of truth, this is just for snappy UI).
  const handleVerifyIdentity = async () => {
    setIdError(''); setIdVerifying(true);
    try {
      const { client_secret } = await api.createIdentitySession();
      const stripe = await stripePromise;
      if (!stripe) throw new Error('Stripe SDK failed to load');
      const { error: stripeErr, verificationSession } = await stripe.verifyIdentity(client_secret);
      if (stripeErr) {
        // Modal closed with an error (camera denied, user aborted, etc).
        setIdError(stripeErr.message || 'Verification was canceled.');
        setIdVerifying(false);
        return;
      }
      // status is one of: requires_input | processing | verified | canceled
      const status = verificationSession?.status;
      if (status === 'canceled' || status === 'requires_input') {
        setIdError(
          status === 'canceled'
            ? 'Verification was canceled. Try again when you have your ID handy.'
            : 'Stripe needs another attempt — please retry.'
        );
        setIdVerifying(false);
        return;
      }
      // 'verified' or 'processing' — webhook should fire within a couple
      // seconds. Poll up to 15 times at 1s intervals.
      for (let attempt = 0; attempt < 15; attempt++) {
        await new Promise(r => setTimeout(r, 1000));
        const s = await api.getIdentityStatus();
        if (s.verified) {
          setIdStatus(s);
          // Prefill the name-edit form with whatever Stripe extracted so
          // the user only has to confirm if names mismatch.
          if (s.verified_name) {
            const parts = s.verified_name.trim().split(/\s+/);
            setIdEditFirstName(parts[0] || '');
            setIdEditLastName(parts.slice(1).join(' ') || '');
          }
          setIdVerifying(false);
          return;
        }
      }
      setIdError('Verification is taking longer than expected. Refresh in a minute, or contact support.');
      setIdVerifying(false);
    } catch (err) {
      if (!recoverFromExpiredSession(err)) {
        setIdError(err.message || 'Verification failed.');
      }
      setIdVerifying(false);
    }
  };

  // Update the user's account name to match the verified ID, then
  // re-fetch identity status — server returns name_match: true once
  // the names align.
  const handleUpdateNameToMatchId = async () => {
    if (!idEditFirstName.trim() || !idEditLastName.trim()) {
      setIdError('Please enter both first and last names.');
      return;
    }
    setIdUpdating(true); setIdError('');
    try {
      const s = await api.updateMyName({
        first_name: idEditFirstName.trim(),
        last_name:  idEditLastName.trim(),
      });
      setIdStatus(s);
      // Mirror into the local userForm so the review step shows the new
      // name immediately if the user navigates there.
      setUserForm(prev => ({ ...prev, firstName: idEditFirstName.trim(), lastName: idEditLastName.trim() }));
    } catch (err) {
      if (!recoverFromExpiredSession(err)) {
        setIdError(err.message || 'Could not update your name.');
      }
    } finally {
      setIdUpdating(false);
    }
  };

  const handleCreateOrg = async () => {
    setError('');
    // The button is disabled without this, but the guard is cheap and the
    // record we write has to mean something.
    if (!baaAccepted) {
      setError('Please review and accept the Business Associate Agreement to continue.');
      return;
    }
    setLoading(true);

    // Medical practices: don't create the org yet. Forward the form data to
    // /signup/checkout where Stripe Checkout runs first; the org is only
    // materialized once checkout.session.completed fires (server-side, via
    // services/checkoutCompletion.js). This avoids ghost orgs in the
    // directory from abandoned-at-Stripe signups. The user's account already
    // exists from step 2, so follow-up emails can still reach them.
    if (orgType === 'medical_practice') {
      // NPI was verified server-side on step 3; we send it along so
      // /signup/checkout can re-verify (defense in depth) and stamp it
      // onto the org row on creation.
      navigate('/signup/checkout', { state: { orgForm: { ...orgForm, npi, provider_type: providerType, baa_version: BAA_VERSION } } });
      return;
    }

    // Law firms are free — create the org immediately. Route through the
    // /api/signup/create-law-firm endpoint so the Stripe Identity gate is
    // re-checked server-side (same defense-in-depth pattern as the
    // medical_practice /checkout path above). Capture the returned org id
    // so we can geocode reliably by id (not by name).
    let newOrgId;
    try {
      const ref = getReferral();
      const resp = await api.createLawFirm({
        type: orgType, // 'law_firm' or (invite-only) 'marketer'
        name: orgForm.name,
        address_line1: orgForm.address_line1 || null,
        city: orgForm.city || null,
        state: orgForm.state || null,
        zip: orgForm.zip || null,
        phone: orgForm.phone || null,
        // Referral attribution — stamped on the org server-side.
        referral_source: ref?.code || null,
        referral_meta: ref || null,
        // Law firms were never shown a BAA yet were stamped as having
        // accepted one. Now they see it and this reflects a real act.
        baa_version: BAA_VERSION,
      });
      newOrgId = resp.org_id;
    } catch (err) {
      if (!recoverFromExpiredSession(err)) setError(err.message);
      setLoading(false); return;
    }

    // Geocode synchronously. We hit the server endpoint with the new org
    // id so the update is reliable (the old fire-and-forget by-name path
    // silently failed if the user navigated away mid-request). If Mapbox
    // genuinely can't resolve the address, the request still returns 200
    // with ok:false and we proceed without coords — the directory map
    // will just skip the pin rather than show a misleading one.
    const address = [orgForm.address_line1, orgForm.city, orgForm.state, orgForm.zip].filter(Boolean).join(', ');
    if (address && newOrgId) {
      try {
        // Through the api client so the auth header rides along — the raw
        // fetch this replaced hit requireAuth with no token and silently
        // 401'd on EVERY law-firm signup, leaving them pinless on the map.
        await api.geocodeOrg({ address, org_id: newOrgId });
      } catch (e) {
        // Geocode failure must never block signup.
        console.warn('geocode call failed, continuing', e);
      }
    }

    // Law firms get the same OWARE welcome handoff as practices (it's free
    // for them too) before landing in the app.
    navigate('/app/welcome', { replace: true });
    window.location.reload();
  };

  return (
    <div className="sup">
      {/* Top bar */}
      <div className="sup-bar">
        <a href="https://www.medilink.vip/" className="sup-brand">
          <img src="/logo-vertical.svg" alt="MediLink" style={{ height: 32, width: 'auto', display: 'block' }} />
        </a>
        <Link to="/login" className="sup-signin">Already have an account? Sign in</Link>
      </div>

      {/* Hero */}
      <div className="sup-hero">
        <h1>
          {step === 1 && <>Welcome. Let&apos;s get you <em>set up</em>.</>}
          {step === 2 && <>{authMode === 'signup' ? <>First, <em>you</em>.</> : <>Welcome <em>back</em>.</>}</>}
          {step === 3 && (orgType === 'marketer' ? <>Welcome to <em>MediLink</em></> : <>Now, your <em>{isFirmLike(orgType) ? firmNoun : 'practice'}</em>.</>)}
          {step === 4 && <>Everything <em>look right</em>?</>}
        </h1>
        <p>
          {step === 1 && "Pick who you are and we'll tailor the rest. About 5 minutes — most clinics see their first matched referral within 48 hours."}
          {step === 2 && (authMode === 'signup'
            ? "Your name and a login. Account updates go to this email."
            : "Sign in to finish setting up your organization.")}
          {step === 3 && (isFirmLike(orgType)
            ? "The basics, so clinics know who's referring. Everything is editable later in settings."
            : "Where you are and what you do, so the right firms can find you. Everything is editable later in settings.")}
          {step === 4 && (orgType === 'medical_practice'
            ? `Confirm and you're in. ${TRIAL_DAYS} days free, and no card today.`
            : "Confirm and you're live. Free, always.")}
        </p>
      </div>

      {/* The promise, on every step. Clinics worry about the card; firms
          worry about a bill. Answer both before they're asked. */}
      <div className="sup-promise" aria-label="What you're signing up for">
        {(step === 1 ? [
          ['Free', 'for law firms, always'],
          [`${TRIAL_DAYS} days`, 'free for clinics'],
          ['No card', 'to start'],
        ] : orgType === 'medical_practice' ? [
          [`${TRIAL_DAYS} days`, 'free, full access'],
          ['No card', 'to start'],
          ['Cancel', 'anytime'],
        ] : [
          ['Free', 'always'],
          ['No card', 'ever'],
          ['5 min', 'to set up'],
        ]).map(([big, small]) => (
          <span key={big + small} className="sup-promise-item"><b>{big}</b> {small}</span>
        ))}
      </div>

      {/* Stepper */}
      <div className="sup-stepper">
        {STEP_LABELS.map((label, i) => {
          const num = i + 1;
          const cls = step === num ? 'is-active' : step > num ? 'is-done' : '';
          return (
            <React.Fragment key={num}>
              <div className={`sup-step-pill ${cls}`}>
                <span className="sup-step-num">{step > num ? <Check size={13} strokeWidth={2.5} /> : num}</span>
                <span className="sup-step-label">{label}</span>
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div className={`sup-step-line ${step > num ? 'is-done' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Card */}
      <div className="sup-card-wrap">
        <div className="sup-card" key={step}>
          {resumed && step >= 3 && (
            <div className="sup-resumed">
              <strong>Welcome back{userForm.firstName ? `, ${userForm.firstName}` : ''}.</strong> We restored your setup{orgForm.name ? ` for ${orgForm.name}` : ''}{resumed.at ? ` from ${new Date(resumed.at).toLocaleDateString()}` : ''} — {idStatus?.verified ? 'your ID verification is on file, ' : ''}pick up right here.
            </div>
          )}
          {/* Step 1: Type */}
          {step === 1 && (
            <>
              <h2>What type of organization are you?</h2>
              <p className="sup-card-sub">Pick one to get started — the rest of signup is tailored to your role.</p>
              <div className="sup-types">
                {ORG_TYPES.map(t => (
                  <button key={t.value} className="sup-type" onClick={() => handleTypeSelect(t.value)}>
                    <div className={`sup-type-icon ${t.iconClass}`}>
                      <t.Icon size={22} strokeWidth={2} />
                    </div>
                    <h3 className="sup-type-name">{t.label}</h3>
                    <p className="sup-type-desc">{t.description}</p>
                    <div className="sup-type-price">
                      <span className="sup-type-price-amount">{t.price}</span>
                      <span className="sup-type-price-note">{t.priceNote}</span>
                    </div>
                    {/* Watch-the-tour chip — nested inside the card button, so
                        stop propagation to open the lightbox without selecting
                        the type. */}
                    {ONBOARDING_VIDEOS[t.value] && (
                      <span
                        className="sup-type-watch"
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setVideoFor(t.value); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); setVideoFor(t.value); } }}
                      >
                        <span className="sup-type-watch-icon"><Play size={10} strokeWidth={2.5} fill="currentColor" /></span>
                        Watch the 40-sec tour
                      </span>
                    )}
                    <ul className="sup-type-features">
                      {t.features.map(f => (
                        <li key={f} className="sup-type-feature">
                          <Check size={12} strokeWidth={2.5} /> {f}
                        </li>
                      ))}
                    </ul>
                    {t.cta && (
                      <span className="sup-type-cta">
                        {t.cta} <ArrowRight size={13} strokeWidth={2.5} />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Step 2: Account */}
          {step === 2 && (
            <>
              <button className="sup-back" onClick={() => { setError(''); setStep(1); }}>
                <ArrowLeft size={12} /> Back
              </button>

              <div className="sup-mode-toggle">
                {[['signup', 'New Account'], ['login', 'Existing Account']].map(([mode, label]) => (
                  <button
                    key={mode}
                    className={`sup-mode-btn ${authMode === mode ? 'is-active' : ''}`}
                    onClick={() => { setAuthMode(mode); setError(''); }}
                    type="button"
                  >
                    {label}
                  </button>
                ))}
              </div>

              <form onSubmit={handleAuth} className="sup-form">
                {error && <div className="sup-error">{error}</div>}
                {authMode === 'signup' && (
                  <>
                    <div className="sup-row">
                      <div>
                        <label className="sup-field-label">First name</label>
                        <input className="sup-input" value={userForm.firstName} onChange={e => updateUser('firstName', e.target.value)} required autoFocus />
                      </div>
                      <div>
                        <label className="sup-field-label">Last name</label>
                        <input className="sup-input" value={userForm.lastName} onChange={e => updateUser('lastName', e.target.value)} required />
                      </div>
                    </div>
                    <div>
                      <label className="sup-field-label">Mobile phone <span style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</span></label>
                      <input type="tel" className="sup-input" placeholder="(555) 123-4567" value={userForm.phone} onChange={e => updateUser('phone', e.target.value)} />
                    </div>
                  </>
                )}
                <div>
                  <label className="sup-field-label">Email</label>
                  <input type="email" className="sup-input" value={userForm.email} onChange={e => updateUser('email', e.target.value)} required autoFocus={authMode === 'login'} />
                </div>
                <div>
                  <label className="sup-field-label">Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="sup-input"
                      style={{ paddingRight: 38 }}
                      placeholder={authMode === 'signup' ? 'Create a password' : ''}
                      value={userForm.password}
                      onChange={e => updateUser('password', e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(s => !s)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      style={{
                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: 'rgba(11,11,20,0.45)', padding: 6, display: 'flex',
                      }}
                    >
                      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>

                {authMode === 'signup' && (
                  <>
                    <div>
                      <label className="sup-field-label">Confirm password</label>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="sup-input"
                        placeholder="Type the same password again"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        required
                      />
                    </div>

                    {/* Live requirements list. Lights up green as each rule is satisfied. */}
                    {(userForm.password.length > 0 || confirmPassword.length > 0) && (
                      <div style={{
                        background: 'rgba(13,167,202,0.04)',
                        border: '1px solid rgba(11,11,20,0.06)',
                        borderRadius: 12,
                        padding: '12px 14px',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '6px 14px',
                      }}>
                        {[
                          { key: 'length',    label: 'At least 8 characters' },
                          { key: 'uppercase', label: 'One uppercase letter' },
                          { key: 'number',    label: 'One number' },
                          { key: 'match',     label: 'Passwords match' },
                        ].map(({ key, label }) => {
                          const ok = pwChecks[key];
                          return (
                            <div key={key} style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              fontSize: 12,
                              color: ok ? '#059669' : 'rgba(11,11,20,0.55)',
                              fontWeight: ok ? 600 : 500,
                            }}>
                              {ok ? <Check size={12} strokeWidth={3} /> : <X size={12} strokeWidth={2.5} />}
                              {label}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                <div className="sup-actions">
                  <button
                    type="submit"
                    disabled={loading || (authMode === 'signup' && !pwAllValid)}
                    className="sup-btn sup-btn-primary"
                  >
                    {loading ? 'Processing…' : 'Continue'} <ArrowRight size={14} />
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Step 3: Practice details */}
          {orgType === 'marketer' && step >= 3 && (
            <MarketerOnboarding
              firstName={userForm.firstName || session?.user?.user_metadata?.first_name || ''}
              lastName={userForm.lastName || session?.user?.user_metadata?.last_name || ''}
              baaVersion={BAA_VERSION}
              onDone={(to) => { navigate(to || '/app/dashboard', { replace: true }); window.location.reload(); }}
            />
          )}

          {step === 3 && orgType !== 'marketer' && (
            <>
              <button className="sup-back" onClick={() => { setError(''); setStep(session ? 1 : 2); }}>
                <ArrowLeft size={12} /> Back
              </button>
              <h2>{isFirmLike(orgType) ? `Set up your ${firmNoun}` : 'Set up your practice'}</h2>
              <p className="sup-card-sub">Almost there. We'll use this to introduce you to potential partners.</p>

              <form
                onSubmit={(e) => {
                  e.preventDefault(); setError('');
                  if (orgType === 'medical_practice' && !providerType) {
                    setError('Please select your provider type.');
                    return;
                  }
                  if (orgType === 'medical_practice' && !npiOk) {
                    setError('Please verify your practice NPI to continue.');
                    return;
                  }
                  // Identity verification is required for both org types
                  // (skipped in the demo environment).
                  if (!identityOk) {
                    setError(idStatus?.name_match === false
                      ? 'Update your account name to match your verified ID, then continue.'
                      : 'Please verify your identity to continue.');
                    return;
                  }
                  setStep(4);
                }}
                className="sup-form"
              >
                {error && <div className="sup-error">{error}</div>}

                {/* Provider type — drives per-location pricing (chiro in a
                    non-PIP state bills standard; everything else premium).
                    Server re-validates on /signup/checkout, so this is UX
                    only. Shown in demo too — it's part of the real flow. */}
                {orgType === 'medical_practice' && (
                  <div>
                    <label className="sup-field-label">
                      Provider type *
                      <span className="sup-field-hint"> · sets your per-location rate</span>
                    </label>
                    <select
                      className="sup-input"
                      value={providerType}
                      onChange={e => setProviderType(e.target.value)}
                      required
                    >
                      <option value="" disabled>Select your provider type…</option>
                      {PROVIDER_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Demo environment: verification is bypassed server-side,
                    so we hide the NPI + identity steps and show a note. */}
                {DEMO && (
                  <div className="sup-npi-ok" style={{ marginBottom: 4 }}>
                    <Check size={14} strokeWidth={3} />
                    <div>
                      <strong>Demo environment</strong>
                      <div className="sup-npi-meta">NPI &amp; identity verification are skipped here.</div>
                    </div>
                  </div>
                )}

                {/* NPI verification block — medical_practice only. The
                    server re-verifies on /signup/checkout; this client
                    check is convenience only. */}
                {!DEMO && orgType === 'medical_practice' && (
                  <div className="sup-npi">
                    <label className="sup-field-label">
                      Practice NPI *
                      <span className="sup-field-hint"> · verified live with CMS</span>
                    </label>
                    <div className="sup-npi-row">
                      <input
                        className="sup-input"
                        value={npi}
                        onChange={e => {
                          const v = e.target.value.replace(/[^\d]/g, '').slice(0, 10);
                          setNpi(v);
                          if (npiVerified) resetNpiVerification();
                        }}
                        placeholder="10-digit NPI (practice or your own)"
                        inputMode="numeric"
                        maxLength={10}
                        autoFocus
                        required
                      />
                      <button
                        type="button"
                        onClick={handleVerifyNpi}
                        disabled={npiVerifying || npi.length !== 10}
                        className={`sup-btn ${npiVerified ? 'sup-btn-verified' : 'sup-btn-secondary'}`}
                      >
                        {npiVerifying
                          ? <><Loader2 size={13} className="sup-spin" /> Verifying…</>
                          : npiVerified
                            ? <><Check size={13} /> Verified</>
                            : <><ShieldCheck size={13} /> Verify NPI</>}
                      </button>
                    </div>
                    {npiError && <div className="sup-npi-err">{npiError}</div>}
                    {npiVerified && npiRecord && (
                      <div className="sup-npi-ok">
                        <Check size={14} strokeWidth={3} />
                        <div>
                          <strong>{npiRecord.legal_name}</strong>
                          <div className="sup-npi-meta">
                            {npiRecord.is_org === false ? 'Individual provider · ' : ''}{npiRecord.specialty || 'Provider'}
                            {npiRecord.address?.city && npiRecord.address?.state &&
                              ` · ${npiRecord.address.city}, ${npiRecord.address.state}`}
                          </div>
                        </div>
                      </div>
                    )}
                    <p className="sup-npi-help">
                      Your practice&apos;s organization NPI (Type 2) or, if you&apos;re a solo provider, your individual NPI (Type 1) — both verify against CMS.
                      No NPI yet? Apply free at{' '}
                      <a href="https://nppes.cms.hhs.gov" target="_blank" rel="noopener noreferrer">nppes.cms.hhs.gov</a>.
                    </p>
                  </div>
                )}

                {/* Identity verification — required for both org types.
                    For medical_practice we wait until NPI passes so the
                    two badges land in order; for law_firm we surface it
                    immediately since there's no NPI prerequisite. */}
                {!DEMO && (isFirmLike(orgType) || (orgType === 'medical_practice' && npiVerified)) && (
                  <div className="sup-npi">
                    <label className="sup-field-label">
                      Verify your identity *
                      <span className="sup-field-hint"> · government ID + selfie</span>
                    </label>

                    {!idStatus?.verified && (
                      <>
                        <p className="sup-npi-help" style={{ margin: '0 0 8px' }}>
                          We use <strong>Stripe Identity</strong> — your ID is uploaded
                          directly to Stripe, never to MediLink. Takes about 30 seconds.
                        </p>
                        <div className="sup-npi-row" style={{ gridTemplateColumns: '1fr' }}>
                          <button
                            type="button"
                            onClick={handleVerifyIdentity}
                            disabled={idVerifying}
                            className="sup-btn sup-btn-secondary"
                          >
                            {idVerifying
                              ? <><Loader2 size={13} className="sup-spin" /> Verifying…</>
                              : <><IdCard size={13} /> Verify identity</>}
                          </button>
                        </div>
                      </>
                    )}

                    {idStatus?.verified && idStatus.name_match && (
                      <div className="sup-npi-ok">
                        <Check size={14} strokeWidth={3} />
                        <div>
                          <strong>Identity verified</strong>
                          <div className="sup-npi-meta">
                            {idStatus.verified_name}
                          </div>
                        </div>
                      </div>
                    )}

                    {idStatus?.verified && idStatus.name_match === false && (
                      <>
                        <div className="sup-npi-err">
                          Your account name <strong>{idStatus.account_name || '(blank)'}</strong> doesn't
                          match the ID we just verified: <strong>{idStatus.verified_name}</strong>.
                          Update your account name to match, then continue.
                        </div>
                        <div className="sup-row" style={{ marginTop: 4 }}>
                          <div>
                            <label className="sup-field-label">First name (from ID)</label>
                            <input
                              className="sup-input"
                              value={idEditFirstName}
                              onChange={e => setIdEditFirstName(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="sup-field-label">Last name (from ID)</label>
                            <input
                              className="sup-input"
                              value={idEditLastName}
                              onChange={e => setIdEditLastName(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="sup-npi-row" style={{ gridTemplateColumns: '1fr', marginTop: 4 }}>
                          <button
                            type="button"
                            onClick={handleUpdateNameToMatchId}
                            disabled={idUpdating}
                            className="sup-btn sup-btn-secondary"
                          >
                            {idUpdating
                              ? <><Loader2 size={13} className="sup-spin" /> Updating…</>
                              : <><Check size={13} /> Use the ID name on my account</>}
                          </button>
                        </div>
                      </>
                    )}

                    {idError && <div className="sup-npi-err">{idError}</div>}
                  </div>
                )}

                <div>
                  <label className="sup-field-label">{orgType === 'law_firm' ? 'Firm name' : orgType === 'marketer' ? 'Business or your name' : 'Practice name'} *</label>
                  <input className="sup-input" value={orgForm.name} onChange={e => updateOrg('name', e.target.value)} required
                    autoFocus={isFirmLike(orgType)}
                    placeholder={orgType === 'law_firm' ? 'e.g., Cherokee Law Group' : orgType === 'marketer' ? 'e.g., Alicia Kirschner Referrals' : 'e.g., National Spine & Joint Partners'} />
                </div>
                <div>
                  <label className="sup-field-label">Phone</label>
                  <input type="tel" className="sup-input" value={orgForm.phone} onChange={e => updateOrg('phone', e.target.value)} placeholder="(555) 555-5555" />
                </div>
                <div>
                  <label className="sup-field-label">Address</label>
                  <AddressAutocomplete
                    value={orgForm.address_line1}
                    onChange={(val) => updateOrg('address_line1', val)}
                    onSelect={(s) => {
                      updateOrg('address_line1', s.address || s.full.split(',')[0]);
                      if (s.city) updateOrg('city', s.city);
                      if (s.state) updateOrg('state', s.state);
                      if (s.zip) updateOrg('zip', s.zip);
                    }}
                    placeholder="Start typing an address…"
                    className="sup-input"
                  />
                </div>
                <div className="sup-row-3">
                  <div>
                    <label className="sup-field-label">City</label>
                    <input className="sup-input" value={orgForm.city} onChange={e => updateOrg('city', e.target.value)} />
                  </div>
                  <div>
                    <label className="sup-field-label">State</label>
                    <input className="sup-input" value={orgForm.state} onChange={e => updateOrg('state', e.target.value)} maxLength={2} placeholder="FL" />
                  </div>
                  <div>
                    <label className="sup-field-label">ZIP</label>
                    <input className="sup-input" value={orgForm.zip} onChange={e => updateOrg('zip', e.target.value)} placeholder="33126" />
                  </div>
                </div>
                <div className="sup-actions">
                  <button
                    type="submit"
                    disabled={
                      !orgForm.name
                      || (orgType === 'medical_practice' && !providerType)
                      || (orgType === 'medical_practice' && !npiOk)
                      || !identityOk
                    }
                    className="sup-btn sup-btn-primary"
                  >
                    Continue to review <ArrowRight size={14} />
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Step 4: Review */}
          {step === 4 && orgType !== 'marketer' && (
            <>
              <button className="sup-back" onClick={() => { setError(''); setStep(3); }}>
                <ArrowLeft size={12} /> Back
              </button>
              <h2>Review and confirm</h2>
              <p className="sup-card-sub">
                {orgType === 'medical_practice'
                  ? `Confirm to create your account. Stripe then confirms your ${TRIAL_DAYS} free days — there's no card field, and nothing is charged today.`
                  : 'Confirm to launch your firm.'}
              </p>

              {error && <div className="sup-error">{error}</div>}

              <div className="sup-review-block">
                <div className="sup-review-label">Account</div>
                <div className="sup-review-row"><span>Name</span><span>{userForm.firstName} {userForm.lastName}</span></div>
                <div className="sup-review-row"><span>Email</span><span>{userForm.email}</span></div>
              </div>

              <div className="sup-review-block">
                <div className="sup-review-label">{orgType === 'law_firm' ? 'Firm' : orgType === 'marketer' ? 'Marketer' : 'Practice'}</div>
                <div className="sup-review-row"><span>Type</span><span>{selectedType?.label}</span></div>
                {orgType === 'medical_practice' && providerType && (
                  <div className="sup-review-row"><span>Provider type</span><span>{PROVIDER_TYPES.find(t => t.value === providerType)?.label}</span></div>
                )}
                <div className="sup-review-row"><span>Name</span><span>{orgForm.name}</span></div>
                {(orgForm.address_line1 || orgForm.city) && (
                  <div className="sup-review-row"><span>Address</span><span>{[orgForm.address_line1, orgForm.city, orgForm.state, orgForm.zip].filter(Boolean).join(', ')}</span></div>
                )}
                {orgForm.phone && (
                  <div className="sup-review-row"><span>Phone</span><span>{orgForm.phone}</span></div>
                )}
              </div>

              {orgType === 'medical_practice' && (
                <div className="sup-review-plan">
                  <div className="sup-review-plan-row">
                    <span className="sup-review-plan-name">MediLink Pro · <em>{TRIAL_DAYS} days on us</em></span>
                    <span className="sup-review-plan-price">{perLocationPrice}<small>/location/mo after</small></span>
                  </div>
                  <TrialTimeline price={perLocationPrice} />
                  <p className="sup-review-plan-note">
                    After your free days, billed monthly via Stripe at {perLocationPrice} per practice location{isPipPriced ? ` (${orgForm.state.toUpperCase()} PIP/no-fault state rate)` : ''}. Your first location is included. Includes the Owner Awareness anti-fraud module at no extra cost.
                  </p>
                </div>
              )}

              {/* HIPAA requires a written contract between a covered entity
                  and its business associate (45 C.F.R. 164.502(e), 164.504(e)).
                  Burying that in "by continuing you agree" fine print — which
                  is what this was, and only for medical practices — is not a
                  presented agreement. Law firms handle the same PHI and need
                  it too. Affirmative, blocking, and linked to text the person
                  can actually read before they tick it. */}
              <label className="sup-baa">
                <input
                  type="checkbox"
                  checked={baaAccepted}
                  onChange={(e) => setBaaAccepted(e.target.checked)}
                />
                <span>
                  I have read and, on behalf of {orgForm.name || 'this organization'}, agree to
                  MediLink's{' '}
                  <a href="/legal/baa" target="_blank" rel="noopener noreferrer">Business Associate Agreement</a>
                  {' '}(version {BAA_VERSION}). I am authorised to enter into this agreement.
                </span>
              </label>

              <button
                onClick={handleCreateOrg}
                disabled={loading || !baaAccepted}
                className="sup-btn sup-btn-primary"
                style={{ width: '100%' }}
              >
                {loading
                  ? (orgType === 'medical_practice' ? 'Loading checkout…' : 'Creating account…')
                  : (orgType === 'medical_practice' ? `Confirm & start my ${TRIAL_DAYS} free days` : 'Confirm & launch')}
                <ArrowRight size={14} />
              </button>
              {orgType === 'medical_practice' && (
                <p className="sup-nocard"><ShieldCheck size={13} strokeWidth={2.2} /> No card on the next screen. Nothing is charged today.</p>
              )}
              <p className="sup-fineprint">
                By continuing, you agree to MediLink's{' '}
                <a href="https://www.medilink.vip/terms" target="_blank" rel="noopener noreferrer">terms</a>{' '}
                and{' '}
                <a href="https://www.medilink.vip/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Walkthrough video lightbox — Vimeo embed, autoplay, Esc / click-out
          / X to close. */}
      {videoFor && ONBOARDING_VIDEOS[videoFor] && (
        <div className="sup-video-overlay" onClick={() => setVideoFor(null)}>
          <div className="sup-video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sup-video-head">
              <span className="sup-video-title">{ONBOARDING_VIDEOS[videoFor].title}</span>
              <button className="sup-video-close" onClick={() => setVideoFor(null)} aria-label="Close video">
                <X size={16} strokeWidth={2.5} />
              </button>
            </div>
            <div className="sup-video-frame">
              <iframe
                src={`https://player.vimeo.com/video/${ONBOARDING_VIDEOS[videoFor].id}?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&title=0&byline=0&portrait=0&dnt=1&loop=1`}
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                referrerPolicy="strict-origin-when-cross-origin"
                title={ONBOARDING_VIDEOS[videoFor].title}
              />
            </div>
            <div className="sup-video-foot">
              Ready when you are —{' '}
              <button
                className="sup-video-start"
                onClick={() => { const v = videoFor; setVideoFor(null); handleTypeSelect(v); }}
              >
                start as a {videoFor === 'law_firm' ? 'law firm' : 'medical practice'} <ArrowRight size={12} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
